import type { ChapterId, StoredProgress } from "../types";

export const CHAPTER_MAX_SCORES: Readonly<Record<ChapterId, number>> = {
  "chapter-1": 25,
  "chapter-2": 15,
  "chapter-3": 40,
  "chapter-4": 20
};

export const CHAPTER_SCORE_BREAKDOWN = {
  "chapter-1": { mastery: 20, process: 5 },
  "chapter-2": { mastery: 12, process: 3 },
  "chapter-3": { experiments: 32, operation: 4, evidence: 4 },
  "chapter-4": { accuracy: 16, process: 4 }
} as const;

export interface ScoreRule {
  chapterId: ChapterId;
  ruleId: string;
  points: number;
  category: string;
}

export interface ScoreGroup {
  chapterId: ChapterId;
  ruleIds: readonly string[];
  maxPoints: number;
  category: string;
}

export const SCORE_GROUPS: readonly ScoreGroup[] = [{
  chapterId: "chapter-4",
  ruleIds: [
    "c4.use.sowing",
    "c4.use.zoning",
    "c4.use.forestry",
    "c4.use.introduction",
    "c4.use.pest-control",
    "c4.use.mountain-observation"
  ],
  maxPoints: 16,
  category: "accuracy"
}];

/**
 * 所有正式探究分都由这些本地规则确定。AI 反馈、提示次数和重试次数不在表中，
 * 因而不会改变掌握分。
 */
export const SCORE_RULES: readonly ScoreRule[] = [
  { chapterId: "chapter-1", ruleId: "c1.classify.biological", points: 4, category: "mastery" },
  { chapterId: "chapter-1", ruleId: "c1.classify.weather", points: 4, category: "mastery" },
  { chapterId: "chapter-1", ruleId: "c1.classify.incidental", points: 4, category: "mastery" },
  { chapterId: "chapter-1", ruleId: "c1.timeline.sequence", points: 8, category: "mastery" },
  { chapterId: "chapter-1", ruleId: "c1.process.observe", points: 1, category: "process" },
  { chapterId: "chapter-1", ruleId: "c1.process.verify", points: 3, category: "process" },
  { chapterId: "chapter-1", ruleId: "c1.process.journal", points: 1, category: "process" },

  { chapterId: "chapter-2", ruleId: "c2.instrument.peach", points: 6, category: "mastery" },
  { chapterId: "chapter-2", ruleId: "c2.instrument.swallow", points: 6, category: "mastery" },
  { chapterId: "chapter-2", ruleId: "c2.process.observe", points: 1, category: "process" },
  { chapterId: "chapter-2", ruleId: "c2.process.read-case", points: 1, category: "process" },
  { chapterId: "chapter-2", ruleId: "c2.process.journal", points: 1, category: "process" },

  { chapterId: "chapter-3", ruleId: "c3.latitude.mastery", points: 8, category: "experiments" },
  { chapterId: "chapter-3", ruleId: "c3.latitude.operated", points: 1, category: "operation" },
  { chapterId: "chapter-3", ruleId: "c3.latitude.evidence", points: 1, category: "evidence" },
  { chapterId: "chapter-3", ruleId: "c3.longitude.mastery", points: 8, category: "experiments" },
  { chapterId: "chapter-3", ruleId: "c3.longitude.operated", points: 1, category: "operation" },
  { chapterId: "chapter-3", ruleId: "c3.longitude.evidence", points: 1, category: "evidence" },
  { chapterId: "chapter-3", ruleId: "c3.altitude.mastery", points: 8, category: "experiments" },
  { chapterId: "chapter-3", ruleId: "c3.altitude.operated", points: 1, category: "operation" },
  { chapterId: "chapter-3", ruleId: "c3.altitude.evidence", points: 1, category: "evidence" },
  { chapterId: "chapter-3", ruleId: "c3.time.mastery", points: 8, category: "experiments" },
  { chapterId: "chapter-3", ruleId: "c3.time.operated", points: 1, category: "operation" },
  { chapterId: "chapter-3", ruleId: "c3.time.evidence", points: 1, category: "evidence" },

  { chapterId: "chapter-4", ruleId: "c4.process.read-p11", points: 1, category: "process" },
  { chapterId: "chapter-4", ruleId: "c4.process.observe", points: 1, category: "process" },
  { chapterId: "chapter-4", ruleId: "c4.process.verify-p11-p12", points: 1, category: "process" },
  { chapterId: "chapter-4", ruleId: "c4.process.journal", points: 1, category: "process" }
] as const;

/** 按已掌握数量等比例计算一个封闭评分项，结果始终在 0 到上限之间。 */
export function calculateMasteryScore(maxScore: number, mastered: number, total: number): number {
  if (!Number.isFinite(maxScore) || maxScore <= 0 || total <= 0) return 0;
  const normalizedMastered = Math.max(0, Math.min(mastered, total));
  return Math.round(maxScore * normalizedMastered / total);
}

/** 根据规则 ID 计算单章分数；重复 ID、未知 ID 与其他章节 ID 均不会重复计分。 */
export function calculateChapterScoreFromRuleIds(
  chapterId: ChapterId,
  masteredRuleIds: Iterable<string>
): number {
  return Object.values(calculateChapterBreakdownFromRuleIds(chapterId, masteredRuleIds))
    .reduce((score, points) => score + points, 0);
}

/** 汇总所有场景的掌握规则，计算指定章节的确定性分数。 */
export function calculateChapterScore(progress: StoredProgress, chapterId: ChapterId): number {
  const mastered = Object.values(progress.scenes).flatMap((scene) => scene?.masteredRuleIds ?? []);
  return calculateChapterScoreFromRuleIds(chapterId, mastered);
}

/** 返回各评分类别的得分，供能力档案解释分数来源。 */
export function calculateChapterBreakdown(
  progress: StoredProgress,
  chapterId: ChapterId
): Record<string, number> {
  const masteredRuleIds = Object.values(progress.scenes)
    .flatMap((scene) => scene?.masteredRuleIds ?? []);
  return calculateChapterBreakdownFromRuleIds(chapterId, masteredRuleIds);
}

function calculateChapterBreakdownFromRuleIds(
  chapterId: ChapterId,
  masteredRuleIds: Iterable<string>
): Record<string, number> {
  const mastered = new Set(masteredRuleIds);
  const additive = SCORE_RULES.reduce<Record<string, number>>((result, rule) => {
    if (rule.chapterId === chapterId && mastered.has(rule.ruleId)) {
      result[rule.category] = (result[rule.category] ?? 0) + rule.points;
    }
    return result;
  }, {});
  return SCORE_GROUPS.reduce<Record<string, number>>((result, group) => {
    if (group.chapterId !== chapterId) return result;
    const masteredCount = group.ruleIds.filter((ruleId) => mastered.has(ruleId)).length;
    result[group.category] = (result[group.category] ?? 0)
      + calculateMasteryScore(group.maxPoints, masteredCount, group.ruleIds.length);
    return result;
  }, additive);
}

/** 汇总四章正式探究分；chapterScores 是持久化的派生缓存。 */
export function getExplorationScore(progress: StoredProgress): number {
  return (Object.keys(CHAPTER_MAX_SCORES) as ChapterId[]).reduce((sum, chapterId) => {
    const derived = calculateChapterScore(progress, chapterId);
    const cached = progress.chapterScores[chapterId] ?? 0;
    return sum + Math.min(CHAPTER_MAX_SCORES[chapterId], Math.max(derived, cached));
  }, 0);
}

export function getChapterMaxScore(id: ChapterId): number {
  return CHAPTER_MAX_SCORES[id];
}

export function getFirstTryRate(progress: StoredProgress): number {
  const { firstTryCorrect, firstTryTotal } = progress.metrics;
  return firstTryTotal === 0 ? 0 : Math.round(firstTryCorrect / firstTryTotal * 100);
}
